import { Cog, AlertTriangle } from "lucide-react";

/**
 * UserMachinesGrid — pre-selected grid of the workshop's own CNC machines.
 * Extracted from Step3MachinesPanel as part of the P3 refactor.
 */
export default function UserMachinesGrid({
  t,
  userMachines,
  selectedUserMachineIds,
  toggleUserMachine,
  inferEfficiency,
}) {
  if (userMachines.length === 0) {
    return (
      <div
        className="mb-6 px-4 py-3 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm"
        data-testid="no-user-machines-hint"
      >
        <AlertTriangle size={14} className="inline mr-2 -mt-0.5" />
        {t("newQuote.noUserMachines", {
          defaultValue:
            "Non hai ancora macchine in officina. Aggiungile da Macchine officina per avere preventivi più accurati.",
        })}
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Cog size={14} className="text-emerald-400" />
        <h3 className="font-display font-semibold text-sm text-emerald-300 uppercase tracking-wider">
          {t("newQuote.yourMachines", { defaultValue: "Le tue macchine officina" })}
        </h3>
        <span className="text-xs text-slate-500">
          ({userMachines.length} {t("newQuote.machinesInWorkshop", { defaultValue: "macchine in officina" })})
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {userMachines.map((um) => {
          const checked = selectedUserMachineIds.includes(um.id);
          const eff = inferEfficiency(um);
          const savePct = Math.round((1 - eff.mult) * 100);
          return (
            <label
              key={um.id}
              className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition-all ${
                checked
                  ? "border-emerald-500 bg-emerald-500/10"
                  : "border-emerald-500/30 bg-slate-900/40 hover:border-emerald-500/60"
              }`}
              data-testid={`user-machine-${um.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleUserMachine(um.id, um.category_id)}
                className="mt-1 w-4 h-4 accent-emerald-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display font-semibold text-slate-100 truncate">{um.name}</div>
                  <div className="font-mono-data text-emerald-400 text-sm whitespace-nowrap">€{um.hourly_rate}/h</div>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                  {um.brand && <span>{um.brand}{um.model ? ` · ${um.model}` : ""}</span>}
                  {um.axes ? <span>· {um.axes} {t("newQuote.axes", { defaultValue: "assi" })}</span> : null}
                  {um.precision_um ? <span>· ±{um.precision_um}µm</span> : null}
                  {um.max_workspace_mm ? <span>· {um.max_workspace_mm}</span> : null}
                </div>
                {eff.reasons.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`user-machine-bonus-${um.id}`}>
                    {eff.reasons.map((r) => (
                      <span
                        key={r}
                        className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300"
                      >
                        {r}
                      </span>
                    ))}
                    <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/40 text-blue-300 font-mono-data">
                      −{savePct}% {t("newQuote.cycleTime", { defaultValue: "ciclo" })}
                    </span>
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
